import { useEffect, useState } from "react";
import { Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { fmtMoney, fmtPeriod, useApi } from "@/lib/api";

const PALETTE = ["#0D2B45", "#C9A96E", "#3F7A9A", "#234E70", "#E0CCA3", "#7FA7BF", "#8C6D38", "#5F7183", "#B9CCD8", "#4D3C20", "#A9B4BD", "#6E9A7A", "#9B5D4A", "#2F4A60", "#D8C39A", "#57534E"];
const short = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : `${(v / 1e3).toFixed(0)}K`);

export const Donut = ({ rows, title }) => {
  const data = rows.filter((r) => r.amount > 0.5);
  const total = data.reduce((s, r) => s + r.amount, 0) || 1;
  const legend = data.filter((r) => r.amount / total >= 0.0005);
  return (
    <div className="panel p-5 min-w-0" data-testid="chart-asset-mix">
      <div className="small-label">{title}</div>
      {data.length ? (
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-3">
          <div className="w-40 h-40 shrink-0">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="amount" nameKey="category" innerRadius="58%" outerRadius="100%" stroke="#fff" strokeWidth={1}>
                  {data.map((r, k) => <Cell key={r.category} fill={PALETTE[k % PALETTE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="text-xs space-y-1.5 w-full min-w-0">
            {legend.map((r) => { const k = data.indexOf(r); return (
              <li key={r.category} className="flex items-center gap-2" data-testid={`mix-legend-${k}`}>
                <span className="h-2.5 w-2.5 shrink-0" style={{ background: PALETTE[k % PALETTE.length] }} />
                <span className="truncate flex-1 text-slate-700">{r.category}</span>
                <span className="font-mono text-slate-500">{((r.amount / total) * 100).toFixed(1)}%</span>
              </li>
            ); })}
          </ul>
        </div>
      ) : <p className="text-sm text-slate-400 mt-3">No assets this month end.</p>}
    </div>
  );
};

export const Trend = ({ points }) => (
  <div className="panel p-5 min-w-0" data-testid="chart-net-worth-trend">
    <div className="small-label">Net worth by month end</div>
    <div className="h-44 mt-3">
      <ResponsiveContainer>
        <LineChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#DEDAD0" vertical={false} />
          <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={short} tick={{ fontSize: 10 }} width={52} tickCount={points.length < 2 ? 3 : 5}
            domain={points.length < 2 ? [(d) => d * 0.9, (d) => d * 1.1] : ["auto", "auto"]} />
          <Tooltip formatter={(v) => fmtMoney(v)} labelFormatter={fmtPeriod} />
          <Line type="monotone" dataKey="net" stroke="#3F7A9A" strokeWidth={2} dot={{ r: 3.5, fill: "#C9A96E" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    {points.length < 2 && <p className="text-xs text-slate-400" data-testid="trend-single-note">More points appear as you upload more month ends.</p>}
  </div>
);

export const StatementCharts = ({ entityId, rows, title, period }) => {
  const api = useApi();
  const [points, setPoints] = useState([]);
  useEffect(() => {
    api.get(`/reports/trend/${entityId}`, { params: period ? { upto: period } : {} }).then((r) => setPoints(r.data)).catch(() => setPoints([]));
  }, [api, entityId, period]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="statement-charts">
      <Donut rows={rows} title={title} />
      <Trend points={points} />
    </div>
  );
};
